import { useEffect, useRef, useState } from "react";
import { Copy, CheckCircle, ExternalLink } from "lucide-react";
import type { GatewayProviderMeta } from "../../../constants/gatewayProviders";
import { config } from "../../../config/config";
import { useToast } from "../../../contexts/toast/Context";

interface StepWebhooksProps {
  provider: GatewayProviderMeta;
  gymPublicId: string;
  /** URL de webhook que reporta el backend (BACKEND_PUBLIC_URL); la construcción local con API_URL es solo fallback. */
  backendWebhookUrl?: string | null;
  onUpdateCredentials: () => void;
  onDisconnect: () => void;
}

export const StepWebhooks = ({
  provider,
  gymPublicId,
  backendWebhookUrl,
  onUpdateCredentials,
  onDisconnect,
}: StepWebhooksProps) => {
  const [copied, setCopied] = useState(false);
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { showToast } = useToast();

  useEffect(() => {
    return () => {
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    };
  }, []);

  const webhookUrl =
    backendWebhookUrl ??
    `${config.API_URL}/gateway/webhooks/${provider.key}/${gymPublicId}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(webhookUrl);
      setCopied(true);
      showToast("URL copiada al portapapeles", "success");
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
      copyTimerRef.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      showToast("Error al copiar", "error");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between px-3 py-2.5 rounded-lg bg-global-success">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full shrink-0 bg-[var(--color-success-text)]" />
          <span className="text-size-xs font-medium text-global-success">
            {provider.displayName} conectado
          </span>
        </div>
      </div>

      {provider.key === "mercadopago" && (
        <MercadoPagoWebhookInstructions
          provider={provider}
          webhookUrl={webhookUrl}
          copied={copied}
          onCopy={handleCopy}
        />
      )}

      {provider.key === "flow" && <FlowWebhookInfo />}

      {provider.helpUrl && (
        <a
          href={provider.helpUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-size-sm text-global-accent hover:underline transition-colors duration-200"
        >
          Ir al panel de {provider.displayName}
          <ExternalLink className="w-3.5 h-3.5" />
        </a>
      )}

      <div className="border-t border-global-secondary pt-3 flex flex-col gap-2">
        <button
          type="button"
          onClick={onUpdateCredentials}
          className="w-full btn-compact btn-global-secondary cursor-pointer active:scale-[0.98] transition-transform duration-150 text-size-xs"
        >
          Actualizar credenciales
        </button>
        <button
          type="button"
          onClick={onDisconnect}
          className="w-full px-3 py-2 rounded-lg text-size-xs font-medium text-global-error border border-(--color-error-border) hover:bg-global-error transition-colors duration-200 cursor-pointer"
        >
          Desconectar {provider.displayName}
        </button>
      </div>
    </div>
  );
};

const MercadoPagoWebhookInstructions = ({
  provider,
  webhookUrl,
  copied,
  onCopy,
}: {
  provider: GatewayProviderMeta;
  webhookUrl: string;
  copied: boolean;
  onCopy: () => void;
}) => (
  <>
    <p className="text-size-sm text-global-secondary font-medium">
      Configurar webhooks
    </p>

    <p className="text-size-2xs text-global-muted leading-relaxed">
      Para recibir notificaciones de pagos (incluyendo cobros recurrentes),
      configura los webhooks en el panel de {provider.displayName}:
    </p>

    <ol className="space-y-2.5">
      <li className="flex items-start gap-3">
        <span className="w-6 h-6 rounded-full bg-global-overlay-secondary text-global-accent text-size-2xs font-bold flex items-center justify-center shrink-0 mt-0.5">
          1
        </span>
        <span className="text-size-sm text-global-secondary">
          Ingresa a{" "}
          <span className="font-medium text-global-primary">
            mercadopago.cl/developers
          </span>{" "}
          y selecciona tu aplicacion
        </span>
      </li>

      <li className="flex items-start gap-3">
        <span className="w-6 h-6 rounded-full bg-global-overlay-secondary text-global-accent text-size-2xs font-bold flex items-center justify-center shrink-0 mt-0.5">
          2
        </span>
        <span className="text-size-sm text-global-secondary">
          Ve a{" "}
          <span className="font-medium text-global-primary">
            Webhooks &gt; Configurar notificaciones
          </span>
        </span>
      </li>

      <li className="flex items-start gap-3">
        <span className="w-6 h-6 rounded-full bg-global-overlay-secondary text-global-accent text-size-2xs font-bold flex items-center justify-center shrink-0 mt-0.5">
          3
        </span>
        <div className="space-y-1.5">
          <span className="text-size-sm text-global-secondary">
            En{" "}
            <span className="font-medium text-global-primary">
              URL de produccion
            </span>
            , ingresa:
          </span>
          <WebhookUrlDisplay url={webhookUrl} copied={copied} onCopy={onCopy} />
        </div>
      </li>

      <li className="flex items-start gap-3">
        <span className="w-6 h-6 rounded-full bg-global-overlay-secondary text-global-accent text-size-2xs font-bold flex items-center justify-center shrink-0 mt-0.5">
          4
        </span>
        <span className="text-size-sm text-global-secondary">
          En <span className="font-medium text-global-primary">Eventos</span>,
          selecciona{" "}
          <code className="text-size-2xs px-1 py-0.5 rounded bg-global-overlay-secondary border border-global-secondary text-global-primary font-mono">
            Pagos
          </code>{" "}
          y guarda
        </span>
      </li>
    </ol>
  </>
);

const FlowWebhookInfo = () => (
  <p className="text-size-2xs text-global-muted leading-relaxed">
    Los webhooks se configuran automaticamente al crear cada pago. No se
    requiere configuracion manual.
  </p>
);

const WebhookUrlDisplay = ({
  url,
  copied,
  onCopy,
}: {
  url: string;
  copied: boolean;
  onCopy: () => void;
}) => (
  <div className="flex items-center gap-1.5">
    <code className="flex-1 text-size-2xs px-2 py-1.5 rounded-md bg-global-overlay-secondary border border-global-secondary text-global-accent font-mono break-all">
      {url}
    </code>
    <button
      type="button"
      onClick={onCopy}
      className="shrink-0 p-1.5 rounded-md border border-global-secondary hover:bg-global-overlay-secondary transition-colors"
      title="Copiar URL"
    >
      {copied ? (
        <CheckCircle className="w-3.5 h-3.5 text-global-success" />
      ) : (
        <Copy className="w-3.5 h-3.5 text-global-muted" />
      )}
    </button>
  </div>
);
